import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Text } from '../../../../style-utils/text';
import { ListSkeleton } from '../dashboard-shared-components/load-state';
import {
  Presence,
  Staleable,
  StateSwitch,
  StatusLabel,
  overlayMotion,
  preset,
  scrimMotion,
  useOverlayMotion,
} from '../../../../motion';
/* WP-37 — the shared table engine, replacing this screen's percentage-width flex
   divs. The expandable panel becomes a second row spanning every column, which
   is the standard table shape for it: a <tr> cannot hold a panel as a sibling of
   its own cells. */
import {
  BodyRow,
  CellFlex,
  Table,
  TableCaption,
  TableFrame,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '../../../table/list-table';
import { palette } from '../../../../style-utils/theme';
import { ThreeDots } from '../../../icons/three-dots';
import { DownArrow } from '../../../icons/down-arrow';
import {
  ActionModal,
  ActionTrigger,
  BuildingCheckbox,
  BuildingMeta,
  BuildingName,
  BuildingTextStack,
  BuildingThumb,
  BuildingThumbEmpty,
  BulkBar,
  ButtonContainer,
  ButtonTitle,
  CancelConfirmButton,
  CheckboxSlot,
  CheckTick,
  CheckTickMark,
  Column,
  CountPill,
  DashboardContent,
  DeleteIconCircle,
  DeleteModal,
  EmptyState,
  EmptyStateIcon,
  ErrorBanner,
  ErrorNotice,
  ErrorText,
  ExpandButton,
  ExpansionPanel,
  ModalBackground,
  NameStack,
  NewPortfolioButton,
  PanelActionButton,
  PanelHeaderRow,
  PanelHeading,
  PanelNote,
  PanelReveal,
  Pill,
  PortfoliosListing,
  RetryButton,
  SelectableBuildingRow,
  Title,
} from './dashboard-portfolios.styled';
import { DashboardPortfolioForm } from './dashboard-portfolio-form';
import { DashboardPortfolioAddBuildings } from './dashboard-portfolio-add-buildings';
import { DashboardPortfolioMoveBuilding } from './dashboard-portfolio-move-building';
import {
  deletePortfolio,
  getPortfolio,
  HierarchyApiError,
  listPortfolios,
  setDefaultPortfolio,
} from '../../../../api/hierarchy-client';
import {
  Portfolio,
  PortfolioBuilding,
} from '../../../../interfaces/hierarchy-interfaces';
import { useAuth } from '../../../../context/auth-provider';
import { can, canAll, PERMISSIONS } from '../../../../auth/permissions';
import { useDialogFocus } from '../../../hooks/use-dialog-focus';
import { ModalPortal } from '../../../modal/modal-portal';

// GET /portfolios/:id is only fetched when a row is opened, so each expanded
// portfolio carries its own request state — one portfolio failing to load its
// buildings must not blank out the rest of the table.
interface PortfolioDetailState {
  isLoading: boolean;
  error: string | null;
  buildings: PortfolioBuilding[] | null;
}

// What the move dialog is being opened for. `buildingName` is set only when a
// single row is moving, so the copy can name it instead of counting it.
interface MoveRequest {
  source: Portfolio;
  buildingIds: number[];
  buildingName: string | null;
}

const DEFAULT_PORTFOLIO_DELETE_HINT =
  'The default portfolio cannot be deleted — it is where buildings land when no portfolio is chosen.';

const NON_EMPTY_DELETE_HINT =
  'This portfolio still holds buildings. Use “Move buildings…” to empty it first.';

// WP-29. The server refuses both of these (portfolios.js:596-600 and :611-620),
// and the data that decides them is already on the row — is_default drives the
// Default pill, building_count drives the CountPill.
//
// The sharpest evidence that this was an oversight rather than a decision: on
// this same row, Archive WAS pre-disabled for a non-empty portfolio while
// Delete, guarded by the identical server rule — Property.count at
// portfolios.js:614 and :1134 are the same query — was not. The comment beside
// the Archive control stated the standard outright: "the reason is on the
// control so the rule is never discovered through a 409." Archive is gone now
// (R-20), so Delete inherits both the rule and the comment.
const deleteBlockedReason = (portfolio: Portfolio): string | null => {
  if (portfolio.is_default) {
    return DEFAULT_PORTFOLIO_DELETE_HINT;
  }

  if (portfolio.building_count > 0) {
    return NON_EMPTY_DELETE_HINT;
  }

  return null;
};

// BOTH OF THESE USED TO END IN "Restore it first." — a remedy that no longer
// exists, since R-20 removed the Restore control. An instruction naming a
// control the product does not have is the same defect WP-30 fixed on units:
// the user does what they are told and arrives nowhere. Archived portfolios are
// now only ever legacy rows, so the remedy is to use a live one.
const ARCHIVED_DEFAULT_HINT =
  'An archived portfolio cannot be made the default. Choose an active portfolio instead.';

const ARCHIVED_ADD_HINT =
  'Buildings cannot be moved into an archived portfolio. Choose an active one.';

const buildingLabel = (count: number): string =>
  count === 1 ? '1 building' : `${count} buildings`;

// `status` is a free-text column on properties, so anything that is not an
// explicit 'active' is shown as neutral rather than guessed at.
const buildingTone = (value: string | null): 'success' | 'neutral' =>
  value === 'active' ? 'success' : 'neutral';

const addressLabel = (building: PortfolioBuilding): string =>
  [building.address, building.country].filter(Boolean).join(', ') ||
  'No address on file';

const readErrorMessage = (error: unknown, fallback: string): string =>
  error instanceof HierarchyApiError ? error.message : fallback;

export const DashboardPortfolios: React.FC = () => {
  const { permissions }: { permissions: string[] } = useAuth();
  const canCreatePortfolio = can(permissions, PERMISSIONS.PORTFOLIO_CREATE);
  const canEditPortfolio = can(permissions, PERMISSIONS.PORTFOLIO_EDIT);
  const canDeletePortfolio = can(permissions, PERMISSIONS.PORTFOLIO_DELETE);
  const canUseRowActions = canEditPortfolio || canDeletePortfolio;
  // Both move endpoints are gated on portfolio:edit AND building:edit (the
  // server ANDs them: a move restructures the portfolio layer and rewrites a
  // building). Offering the control on portfolio:edit alone would hand a role
  // holding only that a guaranteed 403.
  const canMoveBuildings = canAll(permissions, [
    PERMISSIONS.PORTFOLIO_EDIT,
    PERMISSIONS.BUILDING_EDIT,
  ]);

  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [details, setDetails] = useState<Record<number, PortfolioDetailState>>(
    {}
  );

  // Selection belongs to whichever panel is open, and only one is ever open, so
  // a single list is enough — it is cleared whenever the open row changes.
  const [selectedBuildingIds, setSelectedBuildingIds] = useState<number[]>([]);
  // Set by "Move buildings…", which opens the panel with everything already
  // ticked. The buildings may still be in flight, so the selection is applied
  // once the detail lands rather than at click time.
  const [selectAllOnOpenId, setSelectAllOnOpenId] = useState<number | null>(
    null
  );

  const [moveRequest, setMoveRequest] = useState<MoveRequest | null>(null);
  const [addBuildingsTo, setAddBuildingsTo] = useState<Portfolio | null>(null);

  const [openActionsId, setOpenActionsId] = useState<number | null>(null);
  /* WP-37: the actions cell is a <td> now, not a <div>. Only the element type
     changed — `contains()` below is what this ref is for and is unaffected. */
  const openActionsRef = useRef<HTMLTableCellElement | null>(null);
  const rowActionRef = useRef(false);

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [portfolioBeingEdited, setPortfolioBeingEdited] =
    useState<Portfolio | null>(null);

  const [portfolioToDelete, setPortfolioToDelete] = useState<Portfolio | null>(
    null
  );
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const deletingRef = useRef(false);
  const deleteDialogRef = useDialogFocus<HTMLDivElement>(
    Boolean(portfolioToDelete),
    () => {
      if (!deletingRef.current) {
        setPortfolioToDelete(null);
        setDeleteError(null);
      }
    }
  );
  const deleteModalMotion = useOverlayMotion('modal');

  // 'all', not the default 'active'. Nothing in the product archives a
  // portfolio any more (R-20 removed both Archive and Restore), but an
  // organisation that archived one before this shipped still has the row, and
  // filtering to 'active' would strand it: invisible, undeletable, and still
  // holding its name. Listing it keeps Delete reachable so it can be cleared.
  // This is not an inactive-items view in the R-15 sense — there is nothing
  // here to restore, only something to finish removing.
  const loadPortfolios = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);

    try {
      const rows = await listPortfolios('all');

      setPortfolios(rows);
    } catch (error) {
      setLoadError(
        readErrorMessage(error, 'We could not load your portfolios.')
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPortfolios();
  }, [loadPortfolios]);

  // Dismiss the row-actions menu on an outside click or Escape. This is done
  // with document listeners rather than the usual full-screen transparent
  // backdrop because the row carries a held `transform` from its entrance
  // animation, which makes any `position: fixed` child resolve against the row
  // instead of the viewport — such a backdrop would cover one row, not the page.
  useEffect(() => {
    if (openActionsId === null) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!openActionsRef.current?.contains(event.target as Node)) {
        setOpenActionsId(null);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenActionsId(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openActionsId]);

  const loadDetail = useCallback(async (id: number) => {
    setDetails(previous => ({
      ...previous,
      [id]: { isLoading: true, error: null, buildings: null },
    }));

    try {
      const detail = await getPortfolio(id);

      setDetails(previous => ({
        ...previous,
        [id]: {
          isLoading: false,
          error: null,
          buildings: detail.buildings ?? [],
        },
      }));
    } catch (error) {
      setDetails(previous => ({
        ...previous,
        [id]: {
          isLoading: false,
          error: readErrorMessage(
            error,
            'We could not load the buildings in this portfolio.'
          ),
          buildings: null,
        },
      }));
    }
  }, []);

  // Re-fetch only when there is nothing usable cached, so re-opening a row is
  // instant but a previous failure is always retried.
  const ensureDetail = useCallback(
    (id: number) => {
      const cached = details[id];

      if (!cached || (!cached.isLoading && cached.buildings === null)) {
        loadDetail(id);
      }
    },
    [details, loadDetail]
  );

  // "Move buildings…" asks for every building in the row; the list it needs may
  // only arrive a request later, so the tick is applied when the detail settles.
  useEffect(() => {
    if (selectAllOnOpenId === null) {
      return;
    }

    const detail = details[selectAllOnOpenId];

    if (!detail || detail.isLoading) {
      return;
    }

    setSelectedBuildingIds(
      (detail.buildings ?? []).map(building => building.id)
    );
    setSelectAllOnOpenId(null);
  }, [details, selectAllOnOpenId]);

  const toggleExpanded = (id: number) => {
    // The tick boxes belong to the panel that is open; carrying them across to
    // another row would offer to move buildings the user can no longer see.
    setSelectedBuildingIds([]);
    setSelectAllOnOpenId(null);

    if (expandedId === id) {
      setExpandedId(null);

      return;
    }

    setExpandedId(id);
    ensureDetail(id);
  };

  const toggleBuildingSelected = (buildingId: number) => {
    setSelectedBuildingIds(current =>
      current.includes(buildingId)
        ? current.filter(id => id !== buildingId)
        : [...current, buildingId]
    );
  };

  const openAddForm = () => {
    setPortfolioBeingEdited(null);
    setIsFormOpen(true);
  };

  const openEditForm = (portfolio: Portfolio) => {
    setOpenActionsId(null);
    setPortfolioBeingEdited(portfolio);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setPortfolioBeingEdited(null);
  };

  const handleSaved = (saved: Portfolio) => {
    closeForm();
    loadPortfolios();

    // The row's own detail is stale after a rename; drop it so the next expand
    // re-reads rather than showing the pre-edit buildings under a new name.
    setDetails(previous => {
      const next = { ...previous };

      delete next[saved.id];

      return next;
    });
  };

  const openAddBuildings = (portfolio: Portfolio) => {
    setOpenActionsId(null);
    setAddBuildingsTo(portfolio);
  };

  const openMoveOne = (portfolio: Portfolio, building: PortfolioBuilding) => {
    setMoveRequest({
      source: portfolio,
      buildingIds: [building.id],
      buildingName: building.property_name,
    });
  };

  // Takes the ids from the rendered panel rather than reading the selection
  // list directly, so a tick left over from a row that has since moved can
  // never end up in the payload.
  const openMoveSelected = (portfolio: Portfolio, buildingIds: number[]) => {
    setMoveRequest({ source: portfolio, buildingIds, buildingName: null });
  };

  // The row menu's entry point into the same workflow: open the panel with the
  // whole portfolio ticked, which is what someone emptying it before an archive
  // or a delete is after.
  const openMoveBuildings = (portfolio: Portfolio) => {
    setOpenActionsId(null);
    setSelectedBuildingIds([]);
    setSelectAllOnOpenId(portfolio.id);
    setExpandedId(portfolio.id);
    ensureDetail(portfolio.id);
  };

  // A move changes what BOTH ends hold, so building_count in the list and the
  // cached contents of every portfolio involved are stale the moment it lands.
  const handleBuildingsMoved = (affectedPortfolioIds: number[]) => {
    setMoveRequest(null);
    setAddBuildingsTo(null);
    setSelectedBuildingIds([]);
    setSelectAllOnOpenId(null);
    loadPortfolios();

    setDetails(previous => {
      const next = { ...previous };

      affectedPortfolioIds.forEach(id => {
        delete next[id];
      });

      return next;
    });

    // The open panel is the one being looked at — re-read it now instead of
    // leaving a skeleton there until the row is toggled again.
    if (expandedId !== null && affectedPortfolioIds.includes(expandedId)) {
      loadDetail(expandedId);
    }
  };

  // archive / unarchive / default all answer with the updated row and all three
  // can 409 on a rule the menu already guards against — a stale list is the one
  // way to reach one, so the server's sentence is surfaced rather than swallowed.
  const runLifecycleAction = async (
    portfolio: Portfolio,
    action: (id: number) => Promise<Portfolio>,
    fallback: string
  ) => {
    if (rowActionRef.current) {
      return;
    }

    setOpenActionsId(null);
    setActionError(null);
    rowActionRef.current = true;

    try {
      await action(portfolio.id);
      loadPortfolios();
    } catch (error) {
      setActionError(readErrorMessage(error, fallback));
    } finally {
      rowActionRef.current = false;
    }
  };

  const makeDefault = (portfolio: Portfolio) =>
    runLifecycleAction(
      portfolio,
      setDefaultPortfolio,
      'We could not make this the default portfolio.'
    );

  const openDeleteConfirm = (portfolio: Portfolio) => {
    setOpenActionsId(null);
    setDeleteError(null);
    setPortfolioToDelete(portfolio);
  };

  const closeDeleteConfirm = () => {
    if (deletingRef.current) {
      return;
    }

    setPortfolioToDelete(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!portfolioToDelete || deletingRef.current) {
      return;
    }

    deletingRef.current = true;
    setIsDeleting(true);
    setDeleteError(null);

    try {
      await deletePortfolio(portfolioToDelete.id);

      if (expandedId === portfolioToDelete.id) {
        setExpandedId(null);
      }

      setPortfolioToDelete(null);
      loadPortfolios();
    } catch (error) {
      // A 409 here is the server explaining a rule ("still contains 3
      // buildings"), which is information the user acts on — keep the dialog
      // open and show that sentence instead of closing on a silent failure.
      setDeleteError(
        readErrorMessage(error, 'We could not delete this portfolio.')
      );
    } finally {
      deletingRef.current = false;
      setIsDeleting(false);
    }
  };

  // Widths must total 100. The actions column only exists when the user holds
  // an edit or delete permission, so the text columns reclaim its space.
  const widths = canUseRowActions
    ? {
        name: 28,
        code: 12,
        description: 24,
        buildings: 12,
        units: 12,
        actions: 12,
      }
    : {
        name: 32,
        code: 14,
        description: 28,
        buildings: 13,
        units: 13,
        actions: 0,
      };

  const renderSkeleton = () => <ListSkeleton rows={4} label="portfolios" />;

  /* WHAT FILLS THE LIST BOX. A skeleton only while there is nothing to show;
     a reload with rows on screen keeps them and dims them (Staleable), and a
     reload that FAILS keeps them too — the error line says so above them. */
  const listState =
    isLoading && portfolios.length === 0
      ? 'loading'
      : loadError && portfolios.length === 0
      ? 'error'
      : portfolios.length === 0
      ? 'empty'
      : 'ready';

  const renderEmpty = () => (
    <EmptyState>
      <EmptyStateIcon>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M3 8.5a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <path d="M3 12h18" />
        </svg>
      </EmptyStateIcon>
      <Text
        mt={0}
        mb={0}
        fontSize={20}
        fontWeight={700}
        color={palette.ink}
      >
        No portfolios yet
      </Text>
      <Text
        mt={0}
        mb={0}
        fontSize={14}
        color={palette.slate}
        align="center"
        style={{ display: 'block', maxWidth: '340px' }}
      >
        Portfolios group your buildings so access and reporting can be
        managed a set at a time.
      </Text>
      {canCreatePortfolio && (
        <NewPortfolioButton
          id="empty-add-portfolio"
          type="button"
          onClick={openAddForm}
        >
          Add portfolio
        </NewPortfolioButton>
      )}
    </EmptyState>
  );

  // The panel's own header: the heading, plus the way INTO this portfolio. A
  // move into an archived portfolio is refused server-side, so that case is
  // disabled with the reason rather than left to come back as a 409.
  const renderPanelHeader = (portfolio: Portfolio) => (
    <PanelHeaderRow>
      <PanelHeading>Buildings</PanelHeading>
      {canMoveBuildings && (
        <PanelActionButton
          type="button"
          disabled={portfolio.status === 'archived'}
          title={
            portfolio.status === 'archived' ? ARCHIVED_ADD_HINT : undefined
          }
          aria-label={`Add buildings to ${portfolio.name}`}
          onClick={() => openAddBuildings(portfolio)}
        >
          Add buildings…
        </PanelActionButton>
      )}
    </PanelHeaderRow>
  );

  const renderPanel = (portfolio: Portfolio) => {
    const detail = details[portfolio.id];

    if (!detail || detail.isLoading) {
      return (
        <ExpansionPanel>
          {renderPanelHeader(portfolio)}
          <ListSkeleton rows={2} label="buildings" />
        </ExpansionPanel>
      );
    }

    if (detail.error) {
      return (
        <ExpansionPanel>
          <ErrorBanner role="alert">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v4.5" />
              <path d="M12 16h.01" />
            </svg>
            <ErrorText>{detail.error}</ErrorText>
            <RetryButton type="button" onClick={() => loadDetail(portfolio.id)}>
              Try again
            </RetryButton>
          </ErrorBanner>
        </ExpansionPanel>
      );
    }

    const buildings = detail.buildings ?? [];
    const selectedHere = buildings.filter(building =>
      selectedBuildingIds.includes(building.id)
    );

    return (
      <ExpansionPanel>
        {renderPanelHeader(portfolio)}
        {buildings.length === 0 ? (
          <PanelNote>
            No buildings in this portfolio yet.
            {canMoveBuildings && portfolio.status !== 'archived'
              ? ' Use “Add buildings…” to move some in.'
              : ''}
          </PanelNote>
        ) : (
          buildings.map(building => {
            const isSelected = selectedBuildingIds.includes(building.id);

            return (
              <SelectableBuildingRow key={building.id} isSelected={isSelected}>
                {canMoveBuildings && (
                  <CheckboxSlot>
                    <BuildingCheckbox
                      type="checkbox"
                      checked={isSelected}
                      aria-label={`Select ${building.property_name}`}
                      onChange={() => toggleBuildingSelected(building.id)}
                    />
                    {isSelected && (
                      <CheckTick>
                        <CheckTickMark />
                      </CheckTick>
                    )}
                  </CheckboxSlot>
                )}
                {/* The cover photo, from the gallery's is_primary row. It is a
                    presigned URL with an expiry, which is fine here: this panel
                    is fetched when the portfolio is expanded, so the link is
                    always minutes old. Alt is empty on purpose — the building's
                    name is the next element and reading it twice helps nobody
                    on a screen reader. */}
                {building.cover_url ? (
                  <BuildingThumb
                    src={building.cover_url}
                    alt=""
                    loading="lazy"
                  />
                ) : (
                  <BuildingThumbEmpty aria-hidden="true" />
                )}
                <BuildingTextStack>
                  <BuildingName>{building.property_name}</BuildingName>
                  <BuildingMeta>{addressLabel(building)}</BuildingMeta>
                </BuildingTextStack>
                <Pill tone={buildingTone(building.status)}>
                  {building.status === 'active' ? 'Active' : 'Inactive'}
                </Pill>
                {canMoveBuildings && (
                  <PanelActionButton
                    type="button"
                    aria-label={`Move ${building.property_name} to another portfolio`}
                    onClick={() => openMoveOne(portfolio, building)}
                  >
                    Move…
                  </PanelActionButton>
                )}
              </SelectableBuildingRow>
            );
          })
        )}

        {/* A building belongs to exactly one portfolio, so taking it out of
         * this one IS moving it somewhere else — there is no "remove". */}
        {canMoveBuildings && selectedHere.length > 0 && (
          <BulkBar>
            <Text
              mt={0}
              mb={0}
              fontSize={14}
              fontWeight={600}
              color={palette.ink}
            >
              {buildingLabel(selectedHere.length)} selected
            </Text>
            <PanelActionButton
              type="button"
              onClick={() =>
                openMoveSelected(
                  portfolio,
                  selectedHere.map(building => building.id)
                )
              }
            >
              Move to…
            </PanelActionButton>
          </BulkBar>
        )}
      </ExpansionPanel>
    );
  };

  const renderRow = (portfolio: Portfolio, index: number) => {
    const isExpanded = expandedId === portfolio.id;
    const isMenuOpen = openActionsId === portfolio.id;
    const isArchived = portfolio.status === 'archived';

    return (
      <React.Fragment key={portfolio.id}>
        {/* BodyRow carries listItemMotion(index) itself: the capped cascade on
            arrival and, because the rows sit in a Presence, a fade-out on
            removal. layout={false}: no reflow animation on a table row. */}
        <BodyRow index={index} layout={false}>
            <Td data-label="Portfolio">
              <CellFlex>
              <ExpandButton
                type="button"
                isExpanded={isExpanded}
                aria-expanded={isExpanded}
                aria-controls={`portfolio-panel-${portfolio.id}`}
                aria-label={`${isExpanded ? 'Hide' : 'Show'} buildings in ${
                  portfolio.name
                }`}
                onClick={() => toggleExpanded(portfolio.id)}
              >
                <DownArrow />
              </ExpandButton>
              <NameStack>
                <Text
                  mt={0}
                  mb={0}
                  fontSize={14}
                  fontWeight={600}
                  color={palette.ink}
                >
                  {portfolio.name}
                </Text>
                {portfolio.is_default && <Pill tone="brand">Default</Pill>}
                {isArchived && <Pill tone="neutral">Archived</Pill>}
              </NameStack>
              </CellFlex>
            </Td>

            <Td data-label="Code">
              <Text
                mt={0}
                mb={0}
                fontSize={14}
                color={portfolio.code ? palette.ink : palette.slateLight}
              >
                {portfolio.code || '—'}
              </Text>
            </Td>

            <Td data-label="Description">
              <Text
                mt={0}
                mb={0}
                fontSize={14}
                color={portfolio.description ? palette.ink : palette.slateLight}
              >
                {portfolio.description || '—'}
              </Text>
            </Td>

            <Td data-label="Buildings">
              <CountPill>{portfolio.building_count}</CountPill>
            </Td>

            {/* The units rolled up from every building here. A portfolio groups
                buildings and a building groups units, so this is the number
                that says what the group is actually worth. The server omits it
                (rather than sending 0) on responses that did not compute it, so
                an em dash means "not counted", not "none". */}
            <Td data-label="Units">
              {portfolio.unit_count === undefined ? (
                <Text mt={0} mb={0} fontSize={14} color={palette.slateLight}>
                  —
                </Text>
              ) : (
                <CountPill>{portfolio.unit_count}</CountPill>
              )}
            </Td>

            {canUseRowActions && (
              <Td
                data-label="Action"
                style={{ textAlign: 'right' }}
                ref={isMenuOpen ? openActionsRef : undefined}
              >
                <ActionTrigger
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={isMenuOpen}
                  aria-label={`Actions for ${portfolio.name}`}
                  onClick={() =>
                    setOpenActionsId(current =>
                      current === portfolio.id ? null : portfolio.id
                    )
                  }
                >
                  <ThreeDots />
                </ActionTrigger>

                {/* A popover growing from its top-right anchor, shrinking
                    away on close. Dismissal is unchanged: the document
                    listeners above, not a click-catcher. */}
                <Presence>
                {isMenuOpen ? (
                  <ActionModal
                    key="menu"
                    role="menu"
                    {...overlayMotion('popover')}
                    style={{ transformOrigin: 'top right' }}
                  >
                    {canEditPortfolio && (
                      <ButtonContainer
                        type="button"
                        role="menuitem"
                        aria-label={`Edit ${portfolio.name}`}
                        onClick={() => openEditForm(portfolio)}
                      >
                        <ButtonTitle>Edit</ButtonTitle>
                      </ButtonContainer>
                    )}
                    {canEditPortfolio && !portfolio.is_default && (
                      <ButtonContainer
                        type="button"
                        role="menuitem"
                        // An archived portfolio cannot become the fallback that
                        // every unassigned building lands in.
                        disabled={isArchived}
                        title={isArchived ? ARCHIVED_DEFAULT_HINT : undefined}
                        aria-label={
                          isArchived
                            ? ARCHIVED_DEFAULT_HINT
                            : `Make ${portfolio.name} the default portfolio`
                        }
                        onClick={() => makeDefault(portfolio)}
                      >
                        <ButtonTitle>Make default</ButtonTitle>
                      </ButtonContainer>
                    )}
                    {canMoveBuildings && portfolio.building_count > 0 && (
                      <ButtonContainer
                        type="button"
                        role="menuitem"
                        aria-label={`Move buildings out of ${portfolio.name}`}
                        onClick={() => openMoveBuildings(portfolio)}
                      >
                        <ButtonTitle>Move buildings…</ButtonTitle>
                      </ButtonContainer>
                    )}
                    {/* ARCHIVE AND RESTORE ARE GONE (R-20). They were a second
                        destructive vocabulary sitting on the same row as
                        Delete, guarded by the identical server rule, and
                        Restore contradicted "gone is gone" (R-15) outright.
                        The archive/unarchive endpoints still exist server-side
                        and are simply no longer called from the product. */}
                    {canDeletePortfolio && (
                      <ButtonContainer
                        type="button"
                        role="menuitem"
                        isDelete
                        // Both server refusals, stated on the control instead of
                        // after the click. See deleteBlockedReason above.
                        disabled={deleteBlockedReason(portfolio) !== null}
                        title={deleteBlockedReason(portfolio) ?? undefined}
                        aria-label={
                          deleteBlockedReason(portfolio) ??
                          `Delete ${portfolio.name}`
                        }
                        onClick={() => openDeleteConfirm(portfolio)}
                      >
                        <ButtonTitle>Delete</ButtonTitle>
                      </ButtonContainer>
                    )}
                  </ActionModal>
                ) : null}
                </Presence>
              </Td>
            )}
        </BodyRow>

        {/* THE PANEL IS ITS OWN ROW, SPANNING EVERY COLUMN. A <tr> cannot hold a
            panel as a sibling of its own cells, so the expandable shape becomes
            the standard two-row one. `aria-controls` on the expand button
            already points at this id, so the association is unchanged. */}
        {/* THE PANEL UNFOLDS. The row is mounted only while expanded (an
            always-present empty row would draw its own hairline), and the
            reveal inside it carries the collapse variants: height opens from
            zero on expand and, via this Presence, closes to zero before the
            row is removed. initial={false}: a row already open on first paint
            is simply open. */}
        <Presence initial={false}>
          {isExpanded ? (
            <Tr key="panel">
              <Td
                colSpan={canUseRowActions ? 6 : 5}
                id={`portfolio-panel-${portfolio.id}`}
                style={{ padding: 0 }}
              >
                <PanelReveal
                  variants={preset.collapse}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                >
                  {renderPanel(portfolio)}
                </PanelReveal>
              </Td>
            </Tr>
          ) : null}
        </Presence>
      </React.Fragment>
    );
  };

  return (
    <DashboardContent id="dashboard-portfolios-content">
      <PortfoliosListing>
        <Title>
          <Text
            mt={0}
            mb={0}
            fontSize={18}
            fontWeight={700}
            color={palette.ink}
          >
            Portfolios
          </Text>
          <Text mt={0} mb={0} fontSize={13} color={palette.slate}>
            Groups of buildings across your organisation
          </Text>
        </Title>
        {canCreatePortfolio && (
          <NewPortfolioButton
            id="new-portfolio-button"
            type="button"
            onClick={openAddForm}
          >
            Add portfolio
          </NewPortfolioButton>
        )}
      </PortfoliosListing>

      <Column>
        <ErrorNotice message={loadError} onRetry={loadPortfolios} />

        {/* Archive / restore / make-default failures. Kept apart from
         * loadError: the list is on screen and readable, so offering "Try
         * again" for the whole page would be the wrong retry. */}
        <ErrorNotice message={actionError} onRetry={() => setActionError(null)} retryLabel="Dismiss" />

        <StateSwitch state={listState} minHeight={200}>
        {listState === 'loading' ? (
          renderSkeleton()
        ) : listState === 'empty' ? (
          renderEmpty()
        ) : listState === 'ready' ? (
          <Staleable stale={isLoading}>
          <TableFrame>
            <Table>
              <TableCaption>
                Portfolios, the buildings grouped into each one and how many
                units travel with them
              </TableCaption>
              <Thead>
                <Tr>
                  <Th width={widths.name}>Portfolio</Th>
                  <Th width={widths.code}>Code</Th>
                  <Th width={widths.description}>Description</Th>
                  <Th width={widths.buildings}>Buildings</Th>
                  <Th width={widths.units}>Units</Th>
                  {canUseRowActions && (
                    <Th width={widths.actions} style={{ textAlign: 'right' }}>
                      Action
                    </Th>
                  )}
                </Tr>
              </Thead>
              <Tbody>
                {/* A Presence around the rows is what lets a deleted
                    portfolio's row fade out instead of vanishing. */}
                <Presence>
                  {portfolios.map((portfolio, index) =>
                    renderRow(portfolio, index)
                  )}
                </Presence>
              </Tbody>
            </Table>
          </TableFrame>
          </Staleable>
        ) : null}
        </StateSwitch>

        {/* The rows render inside the table above — a <tbody> is where they
            belong, and a sibling list would not be part of the table at all. */}
      </Column>

      {/* Each dialog keyed inside its own Presence, so its scrim and card
          animate OUT on close as well as in on open. */}
      <Presence>
        {isFormOpen ? (
          <DashboardPortfolioForm
            key="portfolio-form"
            portfolio={portfolioBeingEdited}
            onCancel={closeForm}
            onSaved={handleSaved}
          />
        ) : null}
      </Presence>

      <Presence>
        {moveRequest ? (
          <DashboardPortfolioMoveBuilding
            key="portfolio-move"
            source={moveRequest.source}
            buildingIds={moveRequest.buildingIds}
            buildingName={moveRequest.buildingName}
            onCancel={() => setMoveRequest(null)}
            onMoved={handleBuildingsMoved}
          />
        ) : null}
      </Presence>

      <Presence>
        {addBuildingsTo ? (
          <DashboardPortfolioAddBuildings
            key="portfolio-add-buildings"
            portfolio={addBuildingsTo}
            onCancel={() => setAddBuildingsTo(null)}
            onMoved={handleBuildingsMoved}
          />
        ) : null}
      </Presence>

      <Presence>
      {portfolioToDelete ? (
        <ModalPortal key="portfolio-delete">
          <ModalBackground
            {...scrimMotion}
            id="portfolio-delete-background"
            onMouseDown={closeDeleteConfirm}
          >
            <DeleteModal
              {...deleteModalMotion}
              ref={deleteDialogRef}
              id="portfolio-delete-modal"
              tabIndex={-1}
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="portfolio-delete-heading"
              onMouseDown={event => event.stopPropagation()}
            >
              <DeleteIconCircle>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.6}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M4 7h16" />
                  <path d="M10 11v6M14 11v6" />
                  <path d="M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" />
                  <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                </svg>
              </DeleteIconCircle>
              <Text
                id="portfolio-delete-heading"
                mt={0}
                mb={0}
                fontSize={20}
                fontWeight={700}
                color={palette.ink}
              >
                Delete portfolio
              </Text>
              <Text
                mt={8}
                mb={24}
                fontSize={14}
                color={palette.slate}
                align="center"
                style={{ display: 'block', maxWidth: '300px' }}
              >
                This will remove {portfolioToDelete.name} from your organisation.
                It currently holds{' '}
                {buildingLabel(portfolioToDelete.building_count)}. The record stays in
                the audit log. This cannot be undone from inside the app.
              </Text>

              <ErrorNotice message={deleteError} />

              <CancelConfirmButton
                type="button"
                isConfirm
                disabled={isDeleting}
                aria-busy={isDeleting}
                onClick={confirmDelete}
              >
                <StatusLabel
                  state={isDeleting ? 'busy' : 'idle'}
                  labels={{ idle: 'Delete portfolio', busy: 'Deleting…' }}
                />
              </CancelConfirmButton>
              {/* data-autofocus: this dialog renders the destructive button
                  FIRST in source order, and the focus manager used to land on
                  whatever came first — so opening it put the keyboard on
                  "Delete portfolio" and one Enter destroyed it. The marker
                  moves the landing point to the safe control without reordering
                  the visual layout. */}
              <CancelConfirmButton
                type="button"
                disabled={isDeleting}
                data-autofocus
                onClick={closeDeleteConfirm}
              >
                Cancel
              </CancelConfirmButton>
            </DeleteModal>
          </ModalBackground>
        </ModalPortal>
      ) : null}
      </Presence>
    </DashboardContent>
  );
};
